import React, { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { Plus, ListTodo, LayoutGrid, Table as TableIcon } from "lucide-react";
import { toast } from "react-toastify";
import {
  getLanes,
  getStatuses,
  listUsers,
  createLane,
  updateLane,
  deleteLane,
  reorderLanes,
  createItem,
  updateItem,
  deleteItem,
  reorderItems,
  listTags,
} from "../../services/tarefas";
import connectToSocket from "../../services/socket-io";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import LaneView from "./LaneView";
import ItemCard from "./ItemCard";
import ItemEditModal from "./ItemEditModal";
import TableView from "./TableView";
import EmptyStateCard from "../../components/EmptyStateCard";
import { cn } from "../../lib/utils";

const CORES_PADRAO = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#64748b",
];

const hexToRgba = (hex, alpha) => {
  if (!hex) return null;
  const h = hex.replace("#", "");
  if (h.length !== 6) return null;
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${alpha})`;
};

const deduplicateStatuses = (arr) => {
  const byName = new Map();
  const list = Array.isArray(arr) ? arr : [];
  for (const s of list) {
    const key = String(s.name || "Sem status").trim();
    if (!key) continue;
    if (!byName.has(key)) byName.set(key, s);
  }
  return Array.from(byName.values()).sort(
    (a, b) => (Number(a.position) || 0) - (Number(b.position) || 0)
  );
};

const acharLaneDeItem = (lanes, itemId) => {
  for (const lane of lanes) {
    const items = lane.items || [];
    const idx = items.findIndex((it) => String(it.id) === String(itemId));
    if (idx >= 0) return { lane, idx };
  }
  return null;
};

function Tarefas() {
  const [lanes, setLanes] = useState([]);
  const [tagsCatalogo, setTagsCatalogo] = useState([]);
  const [statusesCatalogo, setStatusesCatalogo] = useState([]);
  const [usersCatalogo, setUsersCatalogo] = useState([]);
  const [modalItemAberto, setModalItemAberto] = useState(null);
  const [modalLaneAberto, setModalLaneAberto] = useState(false);
  const [modalRenameLane, setModalRenameLane] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [tabAtiva, setTabAtiva] = useState("table");
  const [refreshKey, setRefreshKey] = useState(0);

  const [activeId, setActiveId] = useState(null);
  const [activeItem, setActiveItem] = useState(null);

  const [newLaneName, setNewLaneName] = useState("");
  const [newLaneColor, setNewLaneColor] = useState(CORES_PADRAO[0]);
  const [renameName, setRenameName] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const buscarDados = useCallback(async () => {
    try {
      const [lanesData, tagsData, statusesData, usersData] = await Promise.all([
        getLanes(),
        listTags(),
        getStatuses().catch(() => []),
        listUsers().catch(() => []),
      ]);
      setLanes(Array.isArray(lanesData) ? lanesData : []);
      setTagsCatalogo(Array.isArray(tagsData) ? tagsData : []);
      setStatusesCatalogo(deduplicateStatuses(statusesData));
      setUsersCatalogo(Array.isArray(usersData) ? usersData : []);
      setRefreshKey((r) => r + 1);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar tarefas");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    buscarDados();
  }, [buscarDados]);

  useEffect(() => {
    try {
      const socket = connectToSocket();
      if (!socket) return undefined;

      const handler = (payload) => {
        if (!payload) return;
        const acao = String(payload.action || payload.acao || "");
        if (
          acao.includes("lane") ||
          acao.includes("item") ||
          acao.includes("kanban") ||
          acao.includes("reorder")
        ) {
          buscarDados();
        }
      };

      socket.on("personalKanban", handler);
      socket.on("personal-kanban", handler);
      socket.on("kanban", handler);

      return () => {
        try { socket.off("personalKanban", handler); } catch (_) {}
        try { socket.off("personal-kanban", handler); } catch (_) {}
        try { socket.off("kanban", handler); } catch (_) {}
      };
    } catch (e) {
      return undefined;
    }
  }, [buscarDados]);

  const handleDragStart = (event) => {
    const rawId = String(event.active.id);
    setActiveId(rawId);
    if (rawId.startsWith("item:")) {
      const idItem = rawId.slice(5);
      const achado = acharLaneDeItem(lanes, idItem);
      if (achado) setActiveItem(achado.lane.items[achado.idx]);
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveItem(null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveItem(null);

    if (!over) return;

    const activeRaw = String(active.id);
    const overRaw = String(over.id);

    const activeIsItem = activeRaw.startsWith("item:");
    const overIsItem = overRaw.startsWith("item:");

    if (!activeIsItem) return;

    const activeItemId = activeRaw.slice(5);
    const origem = acharLaneDeItem(lanes, activeItemId);
    if (!origem) return;

    if (overIsItem) {
      const overItemId = overRaw.slice(5);
      const destino = acharLaneDeItem(lanes, overItemId);
      if (!destino) return;

      if (String(origem.lane.id) === String(destino.lane.id)) {
        if (origem.idx === destino.idx) return;
        const newItems = arrayMove(origem.lane.items, origem.idx, destino.idx);
        const novaOrdem = newItems.map((it, position) => ({
          id: it.id,
          position,
          laneId: origem.lane.id,
        }));

        setLanes((prev) =>
          prev.map((l) =>
            String(l.id) === String(origem.lane.id) ? { ...l, items: newItems } : l
          )
        );

        try {
          await reorderItems(novaOrdem);
          setRefreshKey((r) => r + 1);
        } catch (err) {
          console.error(err);
          toast.error("Erro ao reordenar");
          buscarDados();
        }
      } else {
        const item = origem.lane.items[origem.idx];
        const origemItems = origem.lane.items.filter(
          (_, i) => i !== origem.idx
        );
        const destinoItems = arrayMove(
          [...destino.lane.items.slice(0, destino.idx), item, ...destino.lane.items.slice(destino.idx)],
          destino.idx,
          destino.idx
        );

        const itemsReorder = [];
        origemItems.forEach((it, position) =>
          itemsReorder.push({ id: it.id, position, laneId: origem.lane.id })
        );
        destinoItems.forEach((it, position) =>
          itemsReorder.push({ id: it.id, position, laneId: destino.lane.id })
        );

        setLanes((prev) =>
          prev.map((l) => {
            if (String(l.id) === String(origem.lane.id))
              return { ...l, items: origemItems };
            if (String(l.id) === String(destino.lane.id))
              return { ...l, items: destinoItems };
            return l;
          })
        );

        try {
          await reorderItems(itemsReorder);
          setRefreshKey((r) => r + 1);
        } catch (err) {
          console.error(err);
          toast.error("Erro ao mover tarefa");
          buscarDados();
        }
      }
    } else if (overRaw.startsWith("lane:")) {
      const laneId = overRaw.slice(5);
      if (String(origem.lane.id) === String(laneId)) return;
      const item = origem.lane.items[origem.idx];
      const origemItems = origem.lane.items.filter(
        (_, i) => i !== origem.idx
      );
      const destinoLane = lanes.find(
        (l) => String(l.id) === String(laneId)
      );
      if (!destinoLane) return;
      const destinoItems = [...(destinoLane.items || []), item];

      const itemsReorder = [];
      origemItems.forEach((it, position) =>
        itemsReorder.push({ id: it.id, position, laneId: origem.lane.id })
      );
      destinoItems.forEach((it, position) =>
        itemsReorder.push({ id: it.id, position, laneId })
      );

      setLanes((prev) =>
        prev.map((l) => {
          if (String(l.id) === String(origem.lane.id))
            return { ...l, items: origemItems };
          if (String(l.id) === String(laneId))
            return { ...l, items: destinoItems };
          return l;
        })
      );

      try {
        await reorderItems(itemsReorder);
        setRefreshKey((r) => r + 1);
      } catch (err) {
        console.error(err);
        toast.error("Erro ao mover tarefa");
        buscarDados();
      }
    }
  };

  const abrirModalNovaLane = () => {
    setNewLaneName("");
    setNewLaneColor(CORES_PADRAO[0]);
    setModalLaneAberto(true);
  };

  const salvarNovaLane = async () => {
    if (!newLaneName.trim()) return;
    try {
      await createLane({
        name: newLaneName.trim(),
        color: newLaneColor,
      });
      toast.success("Coluna criada");
      setModalLaneAberto(false);
      buscarDados();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao criar coluna");
    }
  };

  const abrirRenameLane = (lane) => {
    setRenameName(lane.name || "");
    setModalRenameLane(lane);
  };

  const confirmarRenameLane = async () => {
    if (!modalRenameLane || !renameName.trim()) return;
    try {
      await updateLane(modalRenameLane.id, {
        name: renameName.trim(),
        color: modalRenameLane.color,
      });
      toast.success("Coluna renomeada");
      setModalRenameLane(null);
      buscarDados();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao renomear coluna");
    }
  };

  const confirmarDeleteLane = async (lane) => {
    const tem = lane.items && lane.items.length > 0;
    const msg = tem
      ? `Excluir coluna "${lane.name}" e todas as ${lane.items.length} tarefas dela?`
      : `Excluir coluna "${lane.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteLane(lane.id);
      toast.success("Coluna excluída");
      buscarDados();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao excluir coluna");
    }
  };

  const adicionarItem = (lane) => {
    setModalItemAberto({ laneId: lane.id, item: null });
  };

  const editarItem = (item) => {
    const info = acharLaneDeItem(lanes, item.id);
    setModalItemAberto({
      laneId: info ? info.lane.id : null,
      item,
    });
  };

  const salvarItem = async (form) => {
    if (!modalItemAberto) return;
    const { laneId, item } = modalItemAberto;

    try {
      if (item?.id) {
        const dados = {
          laneId: laneId ?? item.laneId,
          ...form,
        };
        await updateItem(item.id, dados);
        toast.success("Tarefa atualizada");
      } else {
        const dados = {
          laneId,
          ...form,
        };
        await createItem(dados);
        toast.success("Tarefa criada");
      }
      setModalItemAberto(null);
      await buscarDados();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar tarefa");
    }
  };

  const dragOverlayItem =
    activeId && activeId.startsWith("item:") ? activeItem : null;

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col bg-background">
      <Tabs
        defaultValue="table"
        value={tabAtiva}
        onValueChange={setTabAtiva}
        className="flex h-full flex-1 flex-col p-6 pb-2"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-2">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Tarefas
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Organize suas tarefas em tabela estilo Monday ou no Kanban visual.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <TabsList className="bg-muted h-9">
              <TabsTrigger value="table" className="gap-1.5">
                <TableIcon className="h-3.5 w-3.5" />
                Tabela
              </TabsTrigger>
              <TabsTrigger value="kanban" className="gap-1.5">
                <LayoutGrid className="h-3.5 w-3.5" />
                Kanban
              </TabsTrigger>
            </TabsList>
            <Button variant="default" onClick={abrirModalNovaLane} className="ml-1">
              <Plus className="h-4 w-4 mr-2" />
              Nova coluna
            </Button>
          </div>
        </div>

        {carregando ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            Carregando...
          </div>
        ) : (
          <>
            <TabsContent value="table" className="mt-3 flex-1 min-h-0 border-0 focus-visible:ring-0">
              <TableView
                onAddItem={adicionarItem}
                onEditItem={editarItem}
                onRefresh={buscarDados}
                refreshKey={refreshKey}
              />
            </TabsContent>

            <TabsContent value="kanban" className="mt-3 flex-1 min-h-0 border-0 focus-visible:ring-0">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <div className="flex h-full w-full">
                  <div className="flex flex-1 gap-4 overflow-x-auto rounded-2xl bg-slate-800 p-5 shadow-inner">
                    {Array.isArray(lanes) && lanes.length === 0 ? (
                      <div className="flex w-full items-center justify-center">
                        <EmptyStateCard
                          variant="flex"
                          size="lg"
                          intent="primary"
                          icon={ListTodo}
                          titleClassName="text-slate-100"
                          subtitleClassName="max-w-md"
                          className="max-w-lg py-10 rounded-2xl border-2 border-dashed border-slate-600/50 bg-slate-900/40 text-slate-200"
                          iconWrapperClassName="bg-slate-700/70 text-slate-200"
                          title="Nenhuma coluna ainda"
                          subtitle="Comece criando sua primeira coluna de tarefas para organizar o trabalho do time."
                          action={{
                            label: "Criar primeira coluna",
                            icon: Plus,
                            onClick: abrirModalNovaLane,
                          }}
                        />
                      </div>
                    ) : (
                      lanes.map((lane) => (
                        <LaneView
                          key={lane.id}
                          lane={lane}
                          onAddItem={adicionarItem}
                          onEditItem={editarItem}
                          onRenameLane={abrirRenameLane}
                          onDeleteLane={confirmarDeleteLane}
                        />
                      ))
                    )}
                  </div>
                </div>

                <DragOverlay>
                  {dragOverlayItem && (
                    <div style={{ transform: "rotate(2deg)" }}>
                      <ItemCard item={dragOverlayItem} />
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
            </TabsContent>
          </>
        )}
      </Tabs>

      <Dialog open={modalLaneAberto} onOpenChange={(v) => !v && setModalLaneAberto(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nova coluna</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="lane-name">Nome da coluna</Label>
              <Input
                id="lane-name"
                value={newLaneName}
                onChange={(e) => setNewLaneName(e.target.value)}
                placeholder="Ex.: A fazer, Em progresso, Concluído"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2">
                {CORES_PADRAO.map((c) => {
                  const ativo = c === newLaneColor;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewLaneColor(c)}
                      style={{ backgroundColor: c }}
                      className={cn(
                        "h-8 w-8 rounded-full ring-offset-2 ring-offset-background transition-all border border-black/10",
                        ativo && "ring-2 ring-current scale-110"
                      )}
                      aria-label={`Cor ${c}`}
                    />
                  );
                })}
              </div>
              <div
                className="mt-2 rounded-md border p-3 text-sm"
                style={{
                  backgroundColor: hexToRgba(newLaneColor, 0.1),
                  borderLeft: `4px solid ${newLaneColor}`,
                }}
              >
                <span className="font-semibold">
                  {newLaneName || "Nome da coluna"}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalLaneAberto(false)}>
              Cancelar
            </Button>
            <Button onClick={salvarNovaLane} disabled={!newLaneName.trim()}>
              Criar coluna
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!modalRenameLane} onOpenChange={(v) => !v && setModalRenameLane(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Renomear coluna</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rename-lane">Nome</Label>
              <Input
                id="rename-lane"
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setModalRenameLane(null)}>
              Cancelar
            </Button>
            <Button onClick={confirmarRenameLane} disabled={!renameName.trim()}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ItemEditModal
        open={!!modalItemAberto}
        onClose={() => setModalItemAberto(null)}
        initialValues={modalItemAberto?.item || null}
        defaultLaneId={modalItemAberto?.laneId || null}
        lanesCatalogo={lanes}
        statusesCatalogo={statusesCatalogo}
        usersCatalogo={usersCatalogo}
        tagsCatalogo={tagsCatalogo}
        onSave={salvarItem}
        onRefresh={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}

export default Tarefas;
